import { useContext, useMemo } from 'react';

import { useLocationsQuery, LocationFilter } from '@ocx/graphql';
import { useConfiguration } from '@ocx/data-configuration';

import { LocalOffersLocationContext } from '../context';

/** Altria stores in radius after lookup: `'available'` / `'not-available'`; `'unknown'` without coordinates or before `locations` is resolved. */
export type AltriaOffersNearbyStatus = 'unknown' | 'available' | 'not-available';

export type UseAltriaLocationsOptions = {
  /**
   * Whether to skip the query execution
   * @default false
   */
  skip?: boolean;
};

export const useAltriaLocations = (options: UseAltriaLocationsOptions = {}) => {
  const { skip = false } = options;

  const configuration = useConfiguration();
  const localOffersConfig = configuration.config.localOffers;
  const { enabled: isLocalOffersFeatureEnabled } = localOffersConfig;

  // Use useContext directly instead of the hook that throws errors
  // This allows the hook to work even when the context provider is not present
  // We have a separate tech debt ticket to refactor this (see OCST-18281)
  const contextData = useContext(LocalOffersLocationContext);
  const locationState = contextData?.locationState ?? null;
  const currentCoordinates = contextData?.currentCoordinates ?? null;
  const customLocation = contextData?.customLocation ?? null;

  const coordinates = useMemo(() => {
    if (locationState === 'nearby' || locationState === 'all') {
      return currentCoordinates;
    }

    if (locationState === 'custom' && customLocation?.coordinates) {
      return customLocation.coordinates;
    }

    return undefined;
  }, [currentCoordinates, customLocation?.coordinates, locationState]);

  const filter: LocationFilter | undefined = useMemo(() => {
    if (!localOffersConfig.enabled || !coordinates) {
      return undefined;
    }

    const locationFilter: LocationFilter = {
      isAltriaParticipant: true,
      geoLocation: {
        epicenter: {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        },
        radiusInMeters: localOffersConfig.radiusInMeters,
      },
    };

    return locationFilter;
  }, [coordinates, localOffersConfig]);

  const shouldSkip = skip || !isLocalOffersFeatureEnabled || !coordinates;

  const { data, loading, error } = useLocationsQuery({
    variables: {
      filter,
    },
    skip: shouldSkip,
  });

  const locations = useMemo(() => {
    if (!data?.locations?.edges) {
      return null;
    }

    return data.locations.edges.map((edge) => edge.node);
  }, [data]);

  const hasAltriaOffersNearby: AltriaOffersNearbyStatus =
    // eslint-disable-next-line no-nested-ternary
    !coordinates || locations === null ? 'unknown' : locations.length > 0 ? 'available' : 'not-available';

  return {
    locations,
    loading,
    error,
    hasAltriaOffersNearby,
  };
};
